import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { StyledInfo } from './styles';
import TooltipInfoCell from 'components/atoms/TooltipInfoCell';
import { EMarkingTypes } from 'constants/Markings';

interface IRegisteredFirearmTableCard {
  data: ITableDataSource;
  renderDataColor: () => any;
  handleEdit: (item: ITableDataSource) => void;
  isAnyLicenceAuthorityValid: (licenceAuthorityIds: string[]) => boolean;
  hasCustomField: boolean;
  showOnMap: (id: string) => void;
  markedText?: string;
}
interface IInfo {
  data: ITableDataSource;
  t: any;
}

const Info = ({ data, t }: IInfo) => {
  const infoText = [];
  if (data.showNoActiveLicenceWarning) {
    infoText.push(t('registeredFirearms.showNoActiveLicenceWarning.text'));
  }
  if (data.showNoOwnerActiveLicenceWarning) {
    infoText.push(t('registeredFirearms.showNoOwnerActiveLicenceWarning.text'));
  }
  if (data.showNoKeeperActiveLicenceWarning) {
    infoText.push(t('registeredFirearms.showNoKeeperActiveLicenceWarning.text'));
  }
  if (data.showExpiredLinkedWarning) {
    infoText.push(t('registeredFirearms.showExpiredLinkedWarning.text'));
  }
  if (data.showInactiveOwnerOrKeeperWarning) {
    infoText.push(t('registeredFirearms.showInactiveOwnerOrKeeperWarning.text'));
  }

  if (infoText.length === 1) {
    return <span style={{ marginLeft: '10px' }}>{infoText[0]}</span>;
  }
  return (
    <>
      {infoText.map((label) => (
        <div>
          <li style={{ marginLeft: '10px' }}>{label}</li>
        </div>
      ))}
    </>
  );
};

const RegisteredFirearmTableCard = ({
  data,
  renderDataColor,
  handleEdit,
  isAnyLicenceAuthorityValid,
  hasCustomField,
  showOnMap,
  markedText,
}: IRegisteredFirearmTableCard) => {
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();
  const navigate = useNavigate();
  return (
    <Grid
      container
      sx={{
        border: `1px solid ${renderDataColor().border}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: renderDataColor().background,
      }}
    >
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          FireCycle ID
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.fireCycleId)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.serialNumber.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.serialNumber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('accessoriesTable.accordion.exhibitNumber.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.exhibitNumber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.make.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.make)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.model.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.model)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.manufacturer.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.manufacturer)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.calibre.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.caliber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.keeper.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.keeper)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.owner.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.owner)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12, sm: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.accordion.state.column')}
        </Typography>
        {data.state ? (
          <Chips text={emptyFieldHolder(data.state)} variant={renderDataColor().stateField} />
        ) : (
          '-'
        )}
      </Grid>
      <Grid sx={{ p: 2 }} size={{ xs: 12 }}>
        <Grid sx={{ alignItems: "flex-start", gap: 1, display: "flex" }}>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('registeredFirearms.accordion.info.column')}
          </Typography>
          <Icon type={EIconTypes.infoWarning} fontSize={20} />
          {data.markings && (
            <TooltipInfoCell
              iconType={EIconTypes.markings}
              markedText={markedText}
              title={t('registeredFirearms.firearmsDetails.firearmMarkings.section.name')}
              rows={[
                {
                  label: t(
                    'registeredFirearms.firearmsDetails.firearmMarkings.importMarking.label',
                  ),
                  value: data.markings[EMarkingTypes.IMPORT],
                },
                {
                  label: t(
                    'registeredFirearms.firearmsDetails.firearmMarkings.appliedMarking.label',
                  ),
                  value: data.markings[EMarkingTypes.APPLIED],
                },
                {
                  label: t(
                    'registeredFirearms.firearmsDetails.firearmMarkings.deactivationMarking.label',
                  ),
                  value: data.markings[EMarkingTypes.DEACTIVATION],
                },
                {
                  label: t(
                    'registeredFirearms.firearmsDetails.firearmMarkings.exportMarking.label',
                  ),
                  value: data.markings[EMarkingTypes.EXPORT],
                },
                {
                  label: t(
                    'registeredFirearms.firearmsDetails.firearmMarkings.surplusSaleMarking.label',
                  ),
                  value: data.markings[EMarkingTypes.SURPLUS_SALE],
                },
                {
                  label: t(
                    'registeredFirearms.firearmsDetails.firearmMarkings.yearOfTransfer.label',
                  ),
                  value: data?.yearOfTransfer,
                },
                {
                  label: t(
                    'registeredFirearms.firearmsDetails.firearmMarkings.confiscationMarking.label',
                  ),
                  value: data.markings[EMarkingTypes.CONFISCATION],
                },
                {
                  label: t(
                    'registeredFirearms.firearmsDetails.firearmMarkings.yearOfConfiscation.label',
                  ),
                  value: data?.yearOfConfiscation,
                },
              ]}
            />
          )}
        </Grid>
        {(data.showNoActiveLicenceWarning ||
          data.showNoOwnerActiveLicenceWarning ||
          data.showNoKeeperActiveLicenceWarning ||
          data.showExpiredLinkedWarning ||
          data.showInactiveOwnerOrKeeperWarning) && (
            <StyledInfo size={{ xs: 12 }}>
              <Info data={data} t={t} />
            </StyledInfo>
          )}
      </Grid>
      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          }, p: 2
        }}>
        <Grid size={{ xs: 12, sm: 3 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('registeredFirearms.accordion.actions.column')}:{' '}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 9 }} container spacing={1} sx={{ justifyContent: 'end' }}>
          {hasPermissions([EPermission.VIEW_FIREARMS]) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                label={t('registeredFirearms.accordion.details.link')}
                variant={EButtonVariants.outlined}
                onClick={() => {
                  navigate(
                    `${ERouteLinks.ViewRegisteredFirearm.replace(':id', data.id.toString())}`,
                  );
                }}
                size="small"
                fullWidth
              />
            </Grid>
          )}
          {data.isLatLong && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                label={t('registeredFirearms.firearmsLocation.showOnMap.label')}
                variant={EButtonVariants.outlined}
                onClick={() => {
                  showOnMap(data.id.toString());
                }}
                size="small"
                fullWidth
              />
            </Grid>
          )}

          {hasPermissions([EPermission.MODIFY_FIREARM]) &&
            hasCustomField &&
            isAnyLicenceAuthorityValid(data.licenceAuthorityIds) && (
              <Grid size={{ xs: 12, sm: 3 }}>
                <Button
                  label={t('registeredFirearms.accordion.edit.link')}
                  variant={EButtonVariants.outlined}
                  onClick={() => handleEdit(data)}
                  size="small"
                  fullWidth
                />
              </Grid>
            )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export { RegisteredFirearmTableCard };
